import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import {Conversion} from '../../domain/types';
import type {ConvertXMLMetadata} from '../../backend-types/xmlconvert-api';
import {debugLog} from '../../lib/debug';

/** The most word lists one conversion may combine, mirrored from the endpoint's own cap. */
export const MAX_WORDLISTS_PER_CONVERSION = 5;

interface ConvertXMLRequest {
  csvKey: string;
  /** The source media reference. Required and non-blank — see `assertMediaIsNamed`. */
  audioPath: string;
  audioSampleRate: number;
  audioDuration: number;
  projectId?: string;
  mode: 'silence' | 'beep';
  markerMode: 'word' | 'clean' | null; // matches backend MarkerMode type
  /**
   * Up to {@link MAX_WORDLISTS_PER_CONVERSION} word lists to combine, in the order they apply.
   *
   * The server merges and de-duplicates them, so a word carried by three of the five still produces
   * exactly one cut — callers do not need to pre-merge, warn about overlap, or offer reordering
   * (order only decides which list is credited in `metadata.wordlists[].added`).
   *
   * Omitted or `[]` means "nothing chosen" and **falls back to the project's default list** — it is
   * not a way to ask for a conversion that censors nothing. That is `wordList: []`.
   *
   * Replaces the deprecated `wordlistId`, which is never sent from here any more: the two are
   * never merged server-side, so sending both would only make which one won ambiguous.
   */
  wordlistIds?: string[];
  /** Required when `mode` is `'beep'`: that mode puts a second clip in the timeline. */
  beepPath?: string;
  beepSampleRate?: number;
  beepDuration?: number;
  fps?: number;
  wordList?: string[];
  outputFileName: string;
}

/**
 * Every clip the XML names has to name a real file.
 *
 * A media reference that names a file is recoverable — the editor relinks it, or CensorMe Mapper
 * rewrites the path afterwards. One that names nothing at all is not: there is no clip to point at
 * anything, in any editor. Cutting to silence needs the source audio named; beeping needs the beep
 * sound named too, because beep mode lays an actual second piece of media into the timeline.
 *
 * It lives in the thunk because the thunk is the only door — the workspace dialog and the batch
 * screen both validate their own inputs, but this is the invariant that has to hold whatever calls
 * it next. Nothing sends `mode: 'beep'` yet (docs/BACKEND_REQUESTS.md §3); when the beep-sound
 * library lands, this is already here.
 *
 * Shaped exactly like the server's own 400 (`reason: 'missing_media_reference'`, `field`, `message`)
 * so callers have one branch to write for "which field needs fixing", whether the request never
 * left the browser or the server caught something this check didn't.
 *
 * @returns the rejection, or `null` when every referenced clip is named.
 */
function assertMediaIsNamed(params: ConvertXMLRequest): ApiError | null {
  if (!params.audioPath?.trim()) {
    return {
      status: 400,
      reason: 'missing_media_reference',
      field: 'audioPath',
      message: 'This conversion has no source audio file name. An XML that references no media '
        + 'cannot be relinked in your editor afterwards.',
    };
  }

  if (params.mode === 'beep' && !params.beepPath?.trim()) {
    return {
      status: 400,
      reason: 'missing_media_reference',
      field: 'beepPath',
      message: 'Beep mode needs a beep sound file. Without one the XML puts an unnamed clip over '
        + 'every censored word, which no editor can relink. Cut to silence instead, or pick a '
        + 'beep sound.',
    };
  }

  if (!params.outputFileName?.trim()) {
    return {
      status: 400,
      reason: 'missing_output_filename',
      field: 'outputFileName',
      message: 'This conversion has no output filename.',
    };
  }

  return null;
}

interface ConvertXMLResponse {
  metadata: ConvertXMLMetadata;
  conversion: ConversionData; // Full conversion record returned by backend
}

interface ConversionData {
  id: string;
  user_id: string;
  project_id?: string;
  csv_r2_key: string;
  xml_r2_key: string;
  xml_signed_url?: string;
  censored_words: number;
  total_cuts: number;
  mode: string;
  markers: string;
  beep_sound_path?: string;
  /** @deprecated The first list of the set only. Use `wordlist_ids`. */
  wordlist_id?: string;
  /** @deprecated The first list's name only. Use `wordlist_names`. */
  word_list_name?: string;
  // Every list the conversion combined, and their names as they stood at the time. Optional here
  // rather than required as the wire type has them, because a response cached or served by a worker
  // from before multi-list conversions has neither — `convertToConversion` falls back to the
  // deprecated singular pair so those rows still name their list.
  wordlist_ids?: string[];
  wordlist_names?: string[];
  // Added 2026-08-26. Null on every record written before it, which is what the conversions list
  // flags: those rows were made before the backend required a media reference, so whether their
  // XML names its clips was never recorded either way.
  audio_path?: string | null;
  created_at: string;
}

interface ConversionApiResponse {
  conversions: ConversionData[];
}

// Helper to convert API response to domain model
function convertToConversion(data: ConversionData): Conversion {
  // Backend returns relative signed URLs, we need to construct the full URL
  let fullSignedUrl: string | undefined = undefined;
  if (data.xml_signed_url) {
    const apiUrl = getWorkerApiUrl();
    fullSignedUrl = data.xml_signed_url.startsWith('http')
      ? data.xml_signed_url
      : `${apiUrl}${data.xml_signed_url}`;
  }

  return {
    id: data.id,
    projectId: data.project_id,
    transcriptionId: undefined,
    csvPath: data.csv_r2_key,
    xmlPath: data.xml_r2_key, // DEPRECATED: kept for backwards compatibility
    xmlR2Key: data.xml_r2_key,
    xmlSignedUrl: fullSignedUrl,
    censoredWords: data.censored_words,
    totalCuts: data.total_cuts,
    mode: data.mode,
    markers: data.markers,
    beepSoundPath: data.beep_sound_path,
    wordListId: data.wordlist_id,
    wordListName: data.word_list_name,
    // The snapshots are the source of truth for what this XML censored; the deprecated singular
    // fields are the fallback for rows a pre-multi-list worker wrote. Never null either way, so
    // every reader can treat these as plain arrays.
    wordListIds: data.wordlist_ids ?? (data.wordlist_id ? [data.wordlist_id] : []),
    wordListNames: data.wordlist_names ?? (data.word_list_name ? [data.word_list_name] : []),
    // Undefined and null both mean "not recorded"; normalising to null keeps the one check the
    // list makes (`audioPath == null`) from also matching a field the backend simply omitted.
    audioPath: data.audio_path ?? null,
    createdAt: data.created_at,
  };
}

export const convertCsvToXml = createAsyncThunk<
  {metadata: ConvertXMLResponse['metadata']; conversion: Conversion},
  ConvertXMLRequest,
  {rejectValue: ApiError}
>(
  'conversions/convertCsvToXml',
  async (
    params: ConvertXMLRequest,
    {rejectWithValue, dispatch}
  ) => {
    try {
      // Before the token, before the network: an XML whose clip names no file at all is worse than
      // no XML, because nothing in the editor can be pointed at the audio afterwards. Shaped
      // identically to the server's own 400 for this, so `reason`/`field` mean the same thing
      // whichever one fired.
      const unnamedMedia = assertMediaIsNamed(params);
      if (unnamedMedia) {
        return rejectWithValue(unnamedMedia);
      }

      const jwt = await getJWTToken();

      if (!jwt) {
        return rejectWithValue({status: 401, message: 'No authentication token available'});
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/convertxml`;

      debugLog('[Conversions] Making POST request to:', url);

      // Build request body - don't send user_id, let backend extract from JWT
      const body: Record<string, any> = {
        csvKey: params.csvKey,
        audioPath: params.audioPath,
        audioSampleRate: params.audioSampleRate,
        audioDuration: params.audioDuration,
        markerMode: params.markerMode,
        outputFileName: params.outputFileName,
      };

      if (params.projectId !== undefined) {
        body.projectId = params.projectId;
      }
      if (params.fps !== undefined) {
        body.fps = params.fps;
      }
      if (params.wordlistIds !== undefined) {
        body.wordlistIds = params.wordlistIds;
      }
      if (params.wordList !== undefined) {
        body.wordList = params.wordList;
      }
      if (params.beepPath !== undefined) {
        body.beepPath = params.beepPath;
      }
      if (params.beepSampleRate !== undefined) {
        body.beepSampleRate = params.beepSampleRate;
      }
      if (params.beepDuration !== undefined) {
        body.beepDuration = params.beepDuration;
      }

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      debugLog('[Conversions] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Conversions] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        // toApiError already lifts `reason`/`field` (missing_media_reference, invalid_video_editor)
        // and `requiredPlan` (402) out of the body, so every one of those cases is one shape here —
        // callers branch on `reason`/`field` rather than parsing the message.
        return rejectWithValue(toApiError(response.status, errorText, 'Could not convert this file'));
      }

      const data: ConvertXMLResponse = await response.json();
      debugLog('[Conversions] Conversion successful');
      debugLog('[Conversions] XML URL:', data.metadata.xml_url);

      // Convert the conversion record from backend format to frontend format
      const conversion = convertToConversion(data.conversion);

      return {
        metadata: data.metadata,
        conversion,
      };
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(toNetworkError(error, 'Could not convert this file'));
    }
  }
);

/**
 * Fetch all conversions for the user
 */
export const fetchConversions = createAsyncThunk(
  'conversions/fetchAll',
  async (_, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/conversions`;

      debugLog('[Conversions] Fetching all conversions from:', url);

      const response = await apiFetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Conversions] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Conversions] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Conversions API returned error ${response.status}: ${errorText}`);
      }

      const data: ConversionApiResponse = await response.json();
      debugLog('[Conversions] Fetched', data.conversions.length, 'conversions');

      return data.conversions.map(convertToConversion);
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * Fetch conversions for a specific project
 */
export const fetchProjectConversions = createAsyncThunk(
  'conversions/fetchProject',
  async (projectId: string, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/conversions?projectId=${projectId}`;

      debugLog('[Conversions] Fetching project conversions from:', url);

      const response = await apiFetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Conversions] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Conversions] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Conversions API returned error ${response.status}: ${errorText}`);
      }

      const data: ConversionApiResponse = await response.json();
      debugLog('[Conversions] Fetched', data.conversions.length, 'conversions for project:', projectId);

      return data.conversions.map(convertToConversion);
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * Delete a conversion
 */
export const deleteConversion = createAsyncThunk(
  'conversions/delete',
  async (conversionId: string, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/conversions/${conversionId}`;

      debugLog('[Conversions] Deleting conversion:', url);

      const response = await apiFetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Conversions] Delete response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Conversions] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Delete API returned error ${response.status}: ${errorText}`);
      }

      debugLog('[Conversions] Conversion deleted successfully:', conversionId);

      return conversionId;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * Upload a CSV file to the Worker API.
 *
 * The desktop app read the CSV off disk via a Rust `read_file` command; on the web the caller
 * hands us the `File` the user picked.
 */
export const uploadCsvToWorker = createAsyncThunk(
  'conversions/uploadCsv',
  async (
    params: {csvFile: File; projectId?: string; audioName?: string},
    {rejectWithValue, dispatch}
  ) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/upload-csv`;

      debugLog('[Conversions] Uploading CSV to:', url);

      // Create FormData for multipart upload. Content-Type is deliberately not set — the browser
      // has to add its own multipart boundary.
      const formData = new FormData();
      formData.append('csv', params.csvFile, params.csvFile.name);

      if (params.projectId) {
        formData.append('projectId', params.projectId);
      }

      if (params.audioName) {
        formData.append('audioPath', params.audioName);
      }

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
        body: formData,
      });

      debugLog('[Conversions] Upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Conversions] Upload error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        // Handle subscription tier restrictions
        if (response.status === 402 || response.status === 429) {
          try {
            const errorData = JSON.parse(errorText);
            const message = errorData.message || 'Feature unavailable for your plan';
            const requiredPlan = errorData.requiredPlan || 'Pro';
            throw new Error(`${message} (requires ${requiredPlan} plan)`);
          } catch {
            // If JSON parsing fails, use the raw error text
            throw new Error(`Upload API returned error ${response.status}: ${errorText}`);
          }
        }

        throw new Error(`Upload API returned error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      debugLog('[Conversions] CSV uploaded successfully with key:', data.csv_key);

      return {
        csv_key: data.csv_key,
        message: data.message,
      };
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

/**
 * Download XML file for a conversion
 * Uses the new authenticated endpoint: GET /download-conversion/:id
 */
export const downloadConversionXml = createAsyncThunk(
  'conversions/downloadXml',
  async ({conversionId}: {conversionId: string}, {dispatch, rejectWithValue}) => {
    try {
      const apiUrl = getWorkerApiUrl();
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      debugLog('[Conversions] Downloading XML for conversion:', conversionId);

      const response = await apiFetch(`${apiUrl}/rest/v1/download-conversion/${conversionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('[Conversions] Download response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Conversions] Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Download failed with status ${response.status}: ${errorText}`);
      }

      const xmlContent = await response.text();
      debugLog('[Conversions] XML downloaded successfully, length:', xmlContent.length);

      return xmlContent;
    } catch (error) {
      console.error('[Conversions] Download error:', error);

      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);
